/**
 * Inventory Operations Handler
 */

import { supabase } from '../../supabase';
import { ParsedCommand, ExecutionResult } from '../types';

export async function executeInventoryOperation(
  command: ParsedCommand,
  userId: string
): Promise<ExecutionResult> {
  const { intent, entities } = command;

  try {
    switch (intent) {
      case 'query_inventory':
        return await queryInventory(entities.filters, entities.target_home_id, entities.target_user);

      case 'create_inventory_item':
        return await createInventoryItem(entities.new_record, userId);

      case 'update_inventory_item':
        if (!entities.target_id) {
          return {
            success: false,
            error: 'No inventory item ID provided',
            message: 'Please specify which inventory item to update',
          };
        }
        return await updateInventoryItem(entities.target_id, entities.updates);

      default:
        return {
          success: false,
          error: `Unsupported inventory operation: ${intent}`,
          message: 'Operation not supported',
        };
    }
  } catch (error) {
    console.error('Error in inventory operation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      message: 'Failed to execute inventory operation',
    };
  }
}

async function queryInventory(
  filters?: Record<string, any>,
  targetHomeId?: string,
  targetUser?: string
): Promise<ExecutionResult> {
  let query = supabase
    .from('inventory_items')
    .select(`
      id,
      name,
      category,
      purchase_date,
      purchase_price,
      model_number,
      serial_number,
      notes,
      created_at,
      home:homes(
        id,
        address,
        city,
        state,
        owner:profiles(full_name)
      ),
      warranties(
        id,
        provider,
        start_date,
        end_date
      )
    `)
    .order('created_at', { ascending: false });

  // Apply filters
  if (targetHomeId) {
    query = query.eq('home_id', targetHomeId);
  }

  // If target user is specified, look up their homes first
  if (targetUser && !targetHomeId) {
    const { data: homes, error: homesError } = await supabase
      .from('homes')
      .select('id, owner:profiles!inner(full_name)')
      .ilike('profiles.full_name', `%${targetUser}%`);

    if (homesError) {
      throw homesError;
    }

    if (homes && homes.length > 0) {
      const homeIds = homes.map(h => h.id);
      query = query.in('home_id', homeIds);
    } else {
      return {
        success: false,
        error: 'User not found or has no homes',
        message: `No homes found for user "${targetUser}"`,
      };
    }
  }

  if (filters) {
    if (filters.category) {
      query = query.eq('category', filters.category);
    }

    // Filter for expired warranties
    if (filters.expired_warranties) {
      // This would need a more complex query or post-processing
      // For now, we'll fetch all and filter client-side
    }
  }

  const { data, error } = await query.limit(100);

  if (error) {
    throw error;
  }

  // Post-process for expired warranties if needed
  let filteredData = data;
  if (filters?.expired_warranties) {
    const now = new Date();
    filteredData = data?.filter(item => {
      if (!item.warranties || item.warranties.length === 0) return false;
      return item.warranties.some((w: any) => new Date(w.end_date) < now);
    });
  }

  return {
    success: true,
    data: filteredData,
    message: `Found ${filteredData?.length || 0} inventory item${filteredData?.length !== 1 ? 's' : ''}`,
    affectedRecords: filteredData?.length || 0,
    recordDetails: filteredData,
  };
}

async function createInventoryItem(
  newRecord?: Record<string, any>,
  userId?: string
): Promise<ExecutionResult> {
  if (!newRecord) {
    return {
      success: false,
      error: 'No data provided',
      message: 'Please specify the inventory item details',
    };
  }

  // Ensure required fields
  if (!newRecord.name) {
    return {
      success: false,
      error: 'Missing required field: name',
      message: 'Please provide a name for the inventory item',
    };
  }

  if (!newRecord.home_id) {
    return {
      success: false,
      error: 'Missing required field: home_id',
      message: 'Please specify which home this item belongs to',
    };
  }

  // Set defaults
  const recordData = {
    name: newRecord.name,
    category: newRecord.category || 'Other',
    home_id: newRecord.home_id,
    purchase_date: newRecord.purchase_date || null,
    purchase_price: newRecord.purchase_price || null,
    model_number: newRecord.model_number || null,
    serial_number: newRecord.serial_number || null,
    barcode: newRecord.barcode || null,
    notes: newRecord.notes || null,
  };

  const { data, error } = await supabase
    .from('inventory_items')
    .insert(recordData)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Successfully created inventory item "${data.name}"`,
    affectedRecords: 1,
    recordDetails: [data],
  };
}

async function updateInventoryItem(
  id: string,
  updates?: Record<string, any>
): Promise<ExecutionResult> {
  if (!updates || Object.keys(updates).length === 0) {
    return {
      success: false,
      error: 'No updates provided',
      message: 'Please specify what to update',
    };
  }

  // Fetch current record
  const { data: current, error: fetchError } = await supabase
    .from('inventory_items')
    .select('id, name')
    .eq('id', id)
    .single();

  if (fetchError || !current) {
    return {
      success: false,
      error: 'Inventory item not found',
      message: `Inventory item ${id} not found`,
    };
  }

  // Perform update
  const { data, error } = await supabase
    .from('inventory_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Successfully updated inventory item "${current.name}"`,
    affectedRecords: 1,
    recordDetails: [data],
  };
}
